import React from "react";
import { Link } from "react-router-dom";
import { ShoppingCart } from "lucide-react";
import { useCart } from "../contexts/CartContext";
import { MEDIA } from "../lib/api";

export default function ProductCard({ product, style }) {
    const { addItem } = useCart();
    const p = product;
    const price = p.sale_price || p.price;
    const onSale = p.sale_price && p.sale_price < p.price;
    const discount = onSale ? Math.round((1 - p.sale_price / p.price) * 100) : 0;

    return (
        <div
            className="group flex flex-col bg-card rounded-2xl overflow-hidden border border-border/60 hover:border-primary/30 hover:shadow-lg transition-all duration-300 animate-fade-up"
            style={style}
            data-testid={`product-card-${p.slug}`}
        >
            {/* ── Image area ── */}
            <Link to={`/produs/${p.slug}`} className="relative block bg-white aspect-[4/3]">
                {p.images?.length > 0 ? (
                    <img
                        src={MEDIA(p.images[p.thumbnail_index ?? 0] || p.images[0])}
                        alt={p.name}
                        loading="lazy"
                        className="w-full h-full object-contain p-2 group-hover:scale-105 transition-transform duration-500 ease-out"
                    />
                ) : (
                    <div className="w-full h-full flex items-center justify-center">
                        <ShoppingCart className="h-8 w-8 text-border" />
                    </div>
                )}

                {/* Gradient hover overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
                <div className="absolute bottom-3 left-0 right-0 flex justify-center opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300 pointer-events-none">
                    <span className="bg-white/90 backdrop-blur-sm text-foreground text-[11px] font-semibold px-4 py-1.5 rounded-full shadow-md tracking-wide">
                        Vezi detalii →
                    </span>
                </div>

                {/* Badges */}
                <div className="absolute top-2 left-2 flex flex-col gap-1 pointer-events-none">
                    {onSale && (
                        <span className="bg-primary text-primary-foreground text-[10px] font-bold px-2.5 py-0.5 rounded-full shadow-sm">
                            -{discount}%
                        </span>
                    )}
                    {p.is_clearance && (
                        <span className="bg-[hsl(var(--warning))] text-white text-[10px] font-semibold px-2.5 py-0.5 rounded-full shadow-sm">
                            Lichidare
                        </span>
                    )}
                </div>
                {p.featured && !onSale && (
                    <div className="absolute top-2 right-2 pointer-events-none">
                        <span className="bg-foreground/75 backdrop-blur-sm text-background text-[10px] font-medium px-2.5 py-0.5 rounded-full">
                            Popular
                        </span>
                    </div>
                )}
            </Link>

            {/* ── Info area ── */}
            <div className="flex flex-col flex-1 p-3 sm:p-4">
                {(p.brand || p.code) && (
                    <div className="text-[10px] text-muted-foreground font-mono truncate mb-1">
                        {p.brand}{p.brand && p.code ? " · " : ""}{p.code}
                    </div>
                )}
                <Link
                    to={`/produs/${p.slug}`}
                    className="font-semibold text-xs sm:text-sm leading-snug line-clamp-2 hover:text-primary transition-colors flex-1 mb-3"
                >
                    {p.name}
                </Link>

                <div className="flex items-center justify-between gap-2 pt-2 border-t border-border/40">
                    <div className="min-w-0">
                        {onSale && (
                            <div className="text-[10px] text-muted-foreground line-through leading-none mb-0.5">
                                {p.price.toFixed(2)} RON
                            </div>
                        )}
                        <div className="font-heading font-bold text-primary text-sm sm:text-xl leading-none">
                            {price.toFixed(2)}{" "}
                            <span className="text-[10px] sm:text-xs font-normal text-foreground/60">RON</span>
                        </div>
                    </div>
                    <button
                        onClick={() => addItem({
                            product_id: p.product_id,
                            quantity: 1,
                            unit_price: price,
                            name: p.name,
                            image: p.images?.[0] || ""
                        })}
                        className="flex-shrink-0 flex items-center gap-1.5 bg-primary text-primary-foreground rounded-xl px-2.5 py-1.5 sm:px-3 sm:py-2 text-[10px] sm:text-xs font-semibold hover:brightness-110 active:brightness-90 transition-all shadow-sm whitespace-nowrap"
                        data-testid={`add-to-cart-${p.slug}`}
                    >
                        <ShoppingCart className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                        <span>Adaugă</span>
                    </button>
                </div>
            </div>
        </div>
    );
}
